/**
 * Telling somebody their template is ready when they are not looking at it.
 *
 * A page-level `Notification`, not a push through the service worker. The
 * difference matters and is worth stating: this reaches a tab that is in the
 * background or behind another window, which is what "I left the page" means
 * in an app you navigate without reloading. It does NOT reach a tab that has
 * been closed — that needs a web-push from the worker when the job lands, and
 * `app/push/` is wired for the daily standout-post alert rather than for this.
 *
 * ### Permission is one-shot, so it is only ever asked for on a tap
 *
 * A denial cannot be re-asked from the page, which is why `shared/common/
 * utils/push.js` says the same thing about the push prompt. Asking on page
 * load, before anybody has started anything worth being told about, spends
 * the permission on nothing.
 */

const api = () => (typeof window !== 'undefined' && 'Notification' in window ? window.Notification : null);

/** 'unsupported' | 'default' | 'granted' | 'denied' — never throws. */
export function notifyState() {
  const N = api();
  if (!N) return 'unsupported';
  try {
    return N.permission;
  } catch {
    return 'unsupported';
  }
}

/** Ask. Only from a tap. → the new state. */
export async function askToNotify() {
  const N = api();
  if (!N) return 'unsupported';
  try {
    if (N.permission !== 'default') return N.permission;
    return await N.requestPermission();
  } catch {
    return notifyState();
  }
}

/**
 * Show one, if we may and if they are not already looking.
 *
 * The visibility check is the point: a notification for something they can
 * see finish is a second copy of the news. → whether one was shown.
 */
export function notifyDone({ title, body, tag, onClick } = {}) {
  const N = api();
  if (!N || notifyState() !== 'granted') return false;
  if (typeof document !== 'undefined' && !document.hidden) return false;
  try {
    // `tag` collapses repeats: two notifications about one run is worse than
    // none, and the push log in `app/push/` exists for the same reason.
    const n = new N(title, { body, tag, icon: '/icons/icon-192.png' });
    n.onclick = () => {
      try { window.focus(); } catch { /* a closed window cannot be focused */ }
      n.close();
      onClick?.();
    };
    return true;
  } catch {
    return false;
  }
}
